import dagre from 'dagre';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 50;
const START_END_SIZE = 36;

function formatCondition(choice, idx) {
  const ops = [
    'StringEquals',
    'StringGreaterThan',
    'StringLessThan',
    'NumericEquals',
    'NumericGreaterThan',
    'NumericLessThan',
    'BooleanEquals',
    'IsPresent',
    'IsNull',
    'IsString',
    'IsNumeric',
    'IsBoolean',
  ];

  for (let i = 0; i < ops.length; i += 1) {
    if (choice[ops[i]] !== undefined) {
      const variable = (choice.Variable || '').split('.').pop();
      return `${variable} ${ops[i].replace(/([A-Z])/g, ' $1').trim()} ${
        choice[ops[i]]
      }`;
    }
  }

  if (choice.Not) return `Rule ${idx + 1} (Not)`;
  if (choice.And) return `Rule ${idx + 1} (And)`;
  if (choice.Or) return `Rule ${idx + 1} (Or)`;

  return `Rule ${idx + 1}`;
}

/**
 * Converts an ASL (Amazon States Language) definition into
 * React Flow nodes and edges with dagre auto-layout.
 *
 * @param {Object} params
 * @param {string} params.startAt - Name of the starting state
 * @param {Object} params.states - ASL States map
 * @param {Object} [params.executionStatus] - Optional execution status overlay
 * @param {string} [params.executionStatus.status] - Overall status
 * @param {Object} [params.executionStatus.stateStatuses] - Per-state statuses
 * @returns {{ nodes: Array, edges: Array }}
 */
export default function aslToFlow({ startAt, states, executionStatus }) {
  const nodes = [];
  const edges = [];
  let edgeId = 0;

  const makeEdge = (source, target, label) => {
    edgeId += 1;
    const isRunning = executionStatus?.status === 'RUNNING';
    const edge = {
      id: `e-${edgeId}`,
      source,
      target,
      type: 'smoothstep',
      animated: isRunning,
      style: {
        stroke: isRunning ? 'var(--if-info)' : 'var(--if-graph-edge)',
        strokeWidth: 1.5,
      },
    };
    if (label) {
      edge.label = label;
      edge.labelStyle = { fontSize: 10, fill: 'var(--if-ink-2)' };
      edge.labelBgStyle = {
        fill: 'var(--if-surface-1)',
        fillOpacity: 0.85,
        rx: 3,
        ry: 3,
      };
      edge.labelBgPadding = [4, 2];
    }
    return edge;
  };

  // Start node
  nodes.push({
    id: '__start__',
    type: 'startEnd',
    data: { label: 'Start' },
    position: { x: 0, y: 0 },
  });
  edges.push(makeEdge('__start__', startAt));

  // State nodes
  const stateNames = Object.keys(states);
  stateNames.forEach((name) => {
    const state = states[name];
    const stateType = (state.Type || 'Task').toLowerCase();

    nodes.push({
      id: name,
      type: `sfn-${stateType}`,
      data: {
        label: name,
        stateType: state.Type,
        resource: state.Resource,
        comment: state.Comment,
        state,
        executionStatus: executionStatus?.status || null,
        stateStatus: executionStatus?.stateStatuses?.[name] || null,
      },
      position: { x: 0, y: 0 },
    });

    // Edges based on state type
    if (state.Type === 'Choice') {
      (state.Choices || []).forEach((choice, idx) => {
        if (choice.Next) {
          const condLabel = formatCondition(choice, idx);
          edges.push(makeEdge(name, choice.Next, condLabel));
        }
      });
      if (state.Default) {
        edges.push(makeEdge(name, state.Default, 'Default'));
      }
    } else if (state.Next) {
      edges.push(makeEdge(name, state.Next));
    }

    // Catch edges
    if (state.Catch) {
      state.Catch.forEach((c) => {
        if (c.Next) {
          edges.push(makeEdge(name, c.Next, 'Catch'));
        }
      });
    }

    // End state → end node
    if (state.End || state.Type === 'Succeed' || state.Type === 'Fail') {
      if (!nodes.find((n) => n.id === '__end__')) {
        nodes.push({
          id: '__end__',
          type: 'startEnd',
          data: { label: 'End' },
          position: { x: 0, y: 0 },
        });
      }
      if (state.Type !== 'Choice') {
        edges.push(makeEdge(name, '__end__'));
      }
    }
  });

  // Auto-layout with dagre
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', ranksep: 60, nodesep: 40 });

  nodes.forEach((node) => {
    const isStartEnd = node.type === 'startEnd';
    g.setNode(node.id, {
      width: isStartEnd ? START_END_SIZE : NODE_WIDTH,
      height: isStartEnd ? START_END_SIZE : NODE_HEIGHT,
    });
  });

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  const layoutNodes = nodes.map((node) => {
    const pos = g.node(node.id);
    const isStartEnd = node.type === 'startEnd';
    const w = isStartEnd ? START_END_SIZE : NODE_WIDTH;
    const h = isStartEnd ? START_END_SIZE : NODE_HEIGHT;
    return {
      ...node,
      position: {
        x: pos.x - w / 2,
        y: pos.y - h / 2,
      },
    };
  });

  return { nodes: layoutNodes, edges };
}
